import { useState } from 'react';
import api from '../../API';
import FolderDestinationPicker from './FolderDestinationPicker';
import styles from './PathPresenterImport.module.css';

function girderErrorMessage(resp) {
    const data = resp?.data;
    if (data && typeof data === 'object' && data.message) return data.message;
    if (typeof data === 'string' && data) return data;
    return resp?.status ? `Request failed (${resp.status})` : 'Request failed.';
}

function formatDestLabel(dest) {
    if (!dest) return '';
    const path = (dest.pathLabels || []).join(' / ');
    return path || dest.name;
}

/**
 * PathPresenter public-share import widget.
 *
 * Props:
 *   folderId              — optional pre-selected destination folder (hides picker by default)
 *   showDestinationPicker — default !folderId
 *   onSuccess             — optional (result) => void after successful import
 *   className             — optional host layout class
 */
function PathPresenterImport({
    folderId: lockedFolderId,
    showDestinationPicker,
    onSuccess,
    className,
}) {
    const pickerVisible = showDestinationPicker ?? !lockedFolderId;

    const [publicUrl, setPublicUrl] = useState('');
    const [preview, setPreview] = useState(null);
    const [destMode, setDestMode] = useState('existing');
    const [destination, setDestination] = useState(
        lockedFolderId
            ? { id: lockedFolderId, name: 'Selected folder', type: 'folder', pathLabels: [] }
            : null,
    );
    const [newFolderName, setNewFolderName] = useState('');
    const [pickerOpen, setPickerOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);

    const onPreview = async (event) => {
        event.preventDefault();
        setError('');
        setResult(null);
        setPreview(null);
        const url = publicUrl.trim();
        if (!url) {
            setError('Public URL is required.');
            return;
        }
        setBusy(true);
        try {
            const resp = await api.post('pathpresenter/preview', {
                json: { publicUrl: url },
            });
            if (resp.status !== 200) {
                setError(girderErrorMessage(resp));
                return;
            }
            setPreview(resp.data);
            if (resp.data?.slides?.[0]?.folderName && !newFolderName) {
                setNewFolderName(String(resp.data.slides[0].folderName));
            }
        } catch (err) {
            setError(err?.message || 'Preview failed.');
        } finally {
            setBusy(false);
        }
    };

    const onImport = async (event) => {
        event.preventDefault();
        setError('');
        setResult(null);
        if (!preview?.publicUrl) {
            setError('Preview the share first.');
            return;
        }
        const body = { publicUrl: preview.publicUrl };
        if (lockedFolderId && !pickerVisible) {
            body.folderId = lockedFolderId;
        } else if (destMode === 'existing') {
            if (!destination || destination.type !== 'folder') {
                setError('Choose an existing folder.');
                return;
            }
            body.folderId = destination.id;
        } else {
            const name = newFolderName.trim();
            if (!name) {
                setError('Folder name is required.');
                return;
            }
            if (!destination) {
                setError('Choose a parent for the new folder.');
                return;
            }
            body.parentId = destination.id;
            body.parentType = destination.type;
            body.folderName = name;
        }
        setBusy(true);
        try {
            const resp = await api.post('pathpresenter/import', { json: body });
            if (resp.status !== 200) {
                setError(girderErrorMessage(resp));
                return;
            }
            setResult(resp.data);
            onSuccess?.(resp.data);
        } catch (err) {
            setError(err?.message || 'Import failed.');
        } finally {
            setBusy(false);
        }
    };

    const failCount = (result?.errors || []).length;
    const resultFolderId = result?.folder?._id;
    const slideCount = preview?.slideCount ?? 0;

    return (
        <div className={[styles.form, className].filter(Boolean).join(' ')}>
            <h2 className={styles.title}>Import from PathPresenter</h2>
            <p className={styles.hint}>
                Preview a public share, choose a destination, then import every slide.
            </p>

            {!preview ? (
                <form onSubmit={onPreview}>
                    <label className={styles.label} htmlFor="pp-public-url">
                        Public URL
                    </label>
                    <input
                        id="pp-public-url"
                        className={styles.input}
                        type="url"
                        placeholder="https://tenant.pathpresenter.net/public/display?token=…"
                        value={publicUrl}
                        onChange={(e) => setPublicUrl(e.target.value)}
                        disabled={busy}
                    />
                    <button className={styles.button} type="submit" disabled={busy}>
                        {busy ? 'Loading…' : 'Preview'}
                    </button>
                </form>
            ) : (
                <form onSubmit={onImport}>
                    <p className={styles.previewMeta}>
                        {slideCount} slide{slideCount === 1 ? '' : 's'} from {preview.hostname}
                    </p>
                    <ul className={styles.slideList}>
                        {(preview.slides || []).map((s) => (
                            <li key={String(s.id ?? s.name)} className={styles.slideRow}>
                                <span className={styles.slideName}>{s.name}</span>
                                {s.folderName ? (
                                    <span className={styles.slideFolder}>{s.folderName}</span>
                                ) : null}
                            </li>
                        ))}
                    </ul>

                    {pickerVisible ? (
                        <>
                            <fieldset className={styles.modeFieldset}>
                                <legend className={styles.label}>Destination</legend>
                                <label className={styles.radioLabel}>
                                    <input
                                        type="radio"
                                        name="pp-dest-mode"
                                        checked={destMode === 'existing'}
                                        onChange={() => setDestMode('existing')}
                                        disabled={busy}
                                    />
                                    Existing folder
                                </label>
                                <label className={styles.radioLabel}>
                                    <input
                                        type="radio"
                                        name="pp-dest-mode"
                                        checked={destMode === 'new'}
                                        onChange={() => setDestMode('new')}
                                        disabled={busy}
                                    />
                                    New folder under…
                                </label>
                            </fieldset>

                            <div className={styles.destRow}>
                                <span className={styles.destLabel}>
                                    {destination
                                        ? formatDestLabel(destination)
                                        : destMode === 'existing'
                                            ? 'No folder selected'
                                            : 'No parent selected'}
                                </span>
                                <button
                                    type="button"
                                    className={styles.buttonSecondary}
                                    disabled={busy}
                                    onClick={() => setPickerOpen(true)}
                                >
                                    {destination ? 'Change' : 'Browse'}
                                </button>
                            </div>

                            {destMode === 'new' ? (
                                <>
                                    <label className={styles.label} htmlFor="pp-folder-name">
                                        New folder name
                                    </label>
                                    <input
                                        id="pp-folder-name"
                                        className={styles.input}
                                        type="text"
                                        autoComplete="off"
                                        value={newFolderName}
                                        onChange={(e) => setNewFolderName(e.target.value)}
                                        disabled={busy}
                                    />
                                </>
                            ) : null}
                        </>
                    ) : (
                        <p className={styles.hint}>
                            Importing into the current folder.
                        </p>
                    )}

                    <div className={styles.actions}>
                        <button
                            type="button"
                            className={styles.buttonSecondary}
                            disabled={busy}
                            onClick={() => {
                                setPreview(null);
                                setResult(null);
                                setError('');
                            }}
                        >
                            Back
                        </button>
                        <button className={styles.button} type="submit" disabled={busy}>
                            {busy
                                ? 'Importing…'
                                : `Import ${slideCount} slide${slideCount === 1 ? '' : 's'}`}
                        </button>
                    </div>
                </form>
            )}

            {error ? <p className={styles.error}>{error}</p> : null}
            {result ? (
                <p className={styles.success}>
                    Imported {result.slideCount} slide{result.slideCount === 1 ? '' : 's'}
                    {failCount ? `; ${failCount} item${failCount === 1 ? '' : 's'} had errors` : ''}
                    {'. '}
                    {resultFolderId ? (
                        <a href={`/dsa#folder/${resultFolderId}`} className={styles.link}>
                            Open in Digital Slide Archive
                        </a>
                    ) : null}
                </p>
            ) : null}

            {pickerOpen ? (
                <FolderDestinationPicker
                    mode={destMode === 'existing' ? 'folder' : 'parent'}
                    title={destMode === 'existing' ? 'Choose folder' : 'Choose parent'}
                    onCancel={() => setPickerOpen(false)}
                    onSelect={(sel) => {
                        setDestination(sel);
                        setPickerOpen(false);
                    }}
                />
            ) : null}
        </div>
    );
}

export default PathPresenterImport;
